// scheduling/components/reports/LookaheadReport.jsx
// 4-Week Lookahead (DJB, 2026-09-29; Trello #252).
//
// "I keep running into instances where preparers are reaching out about jobs on their
// schedule and I am not aware that they were scheduled on it / have already completed the
// job / hate to be holding time on their schedule that is not correct."
//
// The live counterpart of the Monday JM email (api/rprsDigest). The email is deliberately
// thin — one row per job — and links here for the detail: who is on each job week by week,
// every stale-hold flag, budget / WIP, a By-person view, and one-click links into the grid.
//
// ─── EVERYTHING COMES FROM THE SERVER ───────────────────────────────────────
// Rows, weekly buckets, placeholder spreads and FLAGS are all computed in
// api/rprsReports/lookahead.js, which the email shares. The flags are deliberately NOT
// recomputed here (not even the phase-based "looks done"): if this page and the email each
// derived them, the Monday email could say "3 jobs need attention" and the page show 2.
// The milestone chip shows the server's `phase`, from api/shared/jobPhase.js — the verified
// twin of schedConstants' getJobPhase (scripts/verify-job-phase.js), so it matches My Jobs.
//
// ─── TWO "SCHEDULED" NUMBERS ────────────────────────────────────────────────
// Week columns are whole Mon-Sun weeks (a calendar, including days of this week already
// past). Budget / Remaining / the flags use hours from TODAY ON across every date — the
// same "future scheduled" the My Jobs budget bar uses. The legend says so.
//
// ─── TOOLTIP RULE (same as UnscheduledHoursReport) ─────────────────────────
// Every `title` is written for a job manager: no table names, no function names.
//
// ─── GRID LINKS ─────────────────────────────────────────────────────────────
// gridJobHref lives in utils/reportLinks.js (shared with Scheduled vs Actual; UI twin of
// api/shared/rprsLinks.js). A PE-closed job can't be focused here (its future hours sit past
// the window and the grid's job list excludes closed jobs), so its primary link is My Jobs'
// "Completed with scheduled time" section, which has the one-click clear.

import { Fragment, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUrlState } from '../../../hooks/useUrlState';
import { fetchLookahead } from '../../services/reportsApi';
import { PHASE_LABELS, PHASE_COLORS, BRAND } from '../../utils/schedConstants';
import { useSort, SortableTh, PlainTh } from './SortableTh';
import { exportCsv } from '../../utils/csv';
import { gridJobHref, mondayOf, shortDay, todayISO } from '../../utils/reportLinks';

const WEEK_OPTIONS = [4, 8, 12];

const FLAG_META = {
  looks_done: {
    label: 'Looks done', bg: '#EDE9FE', fg: '#5B21B6',
    tip: 'The job is completed in Practice Engine, in Finalization, or marked Not Doing CY, but still has time scheduled from today on. That time is probably no longer needed.',
  },
  over_budget: {
    label: 'Over budget', bg: '#FEE2E2', fg: '#B91C1C',
    tip: 'Hours already charged plus hours still scheduled add up to more than the budget.',
  },
  past_idd: {
    label: 'Past IDD', bg: '#FFEDD5', fg: '#C2410C',
    tip: 'Some of the scheduled time falls after the internal due date. Either the date is out of date or the work is running late.',
  },
  inactive_staff: {
    label: 'Inactive staff', bg: '#FECACA', fg: '#7F1D1D',
    tip: 'Someone who no longer works here is still scheduled on this job.',
  },
  nothing_scheduled: {
    label: 'Nothing scheduled', bg: '#FEF3C7', fg: '#92400E',
    tip: 'The internal due date falls in this window and nobody is scheduled on the job at all.',
  },
};
const FLAG_CODES = Object.keys(FLAG_META);

const hrs = (n) => {
  const v = Math.round((Number(n) || 0) * 100) / 100;
  return v === 0 ? '' : String(v);
};

const SORT_SPECS = {
  client: { get: (j) => j.clientCode, dir: 'asc' },
  clientName: { get: (j) => j.clientName, dir: 'asc' },
  job: { get: (j) => j.jobName, dir: 'asc' },
  idd: { get: (j) => j.idd, dir: 'asc' },
  jm: { get: (j) => j.managerName, dir: 'asc' },
  total: { get: (j) => j.total, dir: 'desc' },
  budget: { get: (j) => j.budgetHours || null, dir: 'desc' },
  wip: { get: (j) => j.wipTotal, dir: 'desc' },
  remaining: { get: (j) => j.remaining, dir: 'asc' },
  flags: { get: (j) => j.flags.length || null, dir: 'desc' },
};
const PERSON_SORT_SPECS = {
  name: { get: (p) => p.name, dir: 'asc' },
  level: { get: (p) => p.level, dir: 'asc' },
  mine: { get: (p) => p.totalMine, dir: 'desc' },
  all: { get: (p) => p.totalAll, dir: 'desc' },
  jobs: { get: (p) => p.jobCount, dir: 'desc' },
};

function FlagChip({ code, detail, onClick, active, count }) {
  const m = FLAG_META[code];
  if (!m) return null;
  return (
    <span className="sched-rpt-chip"
      style={{
        background: m.bg, color: m.fg, marginRight: 4, cursor: onClick ? 'pointer' : 'default',
        outline: active ? `2px solid ${m.fg}` : 'none',
      }}
      title={detail ? `${detail}. ${m.tip}` : m.tip}
      onClick={onClick}>
      {m.label}{count != null ? ` · ${count}` : ''}
    </span>
  );
}

function WeekCells({ weekly, strong = false }) {
  return weekly.map((h, i) => (
    <td key={i} className="sched-mgr-td-right" style={strong ? { fontWeight: 600 } : undefined}>{hrs(h)}</td>
  ));
}

export default function LookaheadReport() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  // URL-backed with an `l` prefix, like every other report's params, so the Monday email's
  // links (lrole / llead / lflags) and shared URLs land on exactly this view.
  const [view, setView] = useUrlState('lview', 'job', { validate: ['job', 'person'] });
  const [role, setRole] = useUrlState('lrole', 'both', { validate: ['both', 'jm', 'jp'] });
  const [weeks, setWeeks] = useUrlState('lweeks', 4, { validate: WEEK_OPTIONS });
  const [flagsOnly, setFlagsOnly] = useUrlState('lflags', false);
  const [flagCode, setFlagCode] = useUrlState('lflag', '', { validate: ['', ...FLAG_CODES] });
  const [q, setQ] = useUrlState('lq', '');
  const [lead, setLead] = useUrlState('llead', '');

  const [expanded, setExpanded] = useState(() => new Set());
  const sort = useSort(SORT_SPECS, 'client', 'lsort');
  const personSort = useSort(PERSON_SORT_SPECS, 'mine', 'lpsort');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchLookahead({
      weeks,
      role,
      leadStaffId: lead ? Number(lead) : undefined,
      today: todayISO(),
    })
      .then((r) => { if (!cancelled) { setData(r); setError(null); setLoading(false); } })
      .catch((e) => { if (!cancelled) { setError(e.message); setLoading(false); } });
    return () => { cancelled = true; };
  }, [weeks, role, lead]);

  const weekDates = useMemo(() => data?.weeks || [], [data]);
  const weekStart = data?.weekStart || mondayOf(todayISO());

  // Search applies to both views; flags only to jobs.
  const needle = q.trim().toLowerCase();
  const searched = useMemo(() => (data?.jobs || []).filter((j) => !needle
    || [j.clientCode, j.clientName, j.jobName, j.managerName, j.partnerName]
      .some((s) => String(s || '').toLowerCase().includes(needle))), [data, needle]);

  // Flag chip counts are over the searched set (before the flag filter), so a chip always
  // says how many rows clicking it will leave.
  const flagCounts = useMemo(() => {
    const c = {};
    for (const code of FLAG_CODES) c[code] = 0;
    for (const j of searched) for (const f of j.flags) c[f.code] += 1;
    return c;
  }, [searched]);

  const shownJobs = useMemo(() => sort.sortRows(searched.filter((j) => {
    if (flagsOnly && j.flags.length === 0) return false;
    if (flagCode && !j.flags.some((f) => f.code === flagCode)) return false;
    return true;
  })), [searched, flagsOnly, flagCode, sort]);

  const shownPeople = useMemo(() => personSort.sortRows((data?.people || []).filter((p) => !needle
    || String(p.name || '').toLowerCase().includes(needle)
    || p.jobs.some((j) => [j.clientCode, j.clientName, j.jobName]
      .some((s) => String(s || '').toLowerCase().includes(needle))))), [data, needle, personSort]);

  // Headline figures describe the rows on screen (the UnscheduledHoursReport lesson).
  const stats = useMemo(() => {
    const weekTotals = weekDates.map((_, i) => shownJobs.reduce((a, j) => a + (j.weekly[i] || 0), 0));
    return {
      jobs: shownJobs.length,
      hours: Math.round(shownJobs.reduce((a, j) => a + j.total, 0) * 100) / 100,
      flagged: shownJobs.filter((j) => j.flags.length > 0).length,
      staff: new Set(shownJobs.flatMap((j) => j.staff.map((s) => s.staffId))).size,
      weekTotals,
    };
  }, [shownJobs, weekDates]);

  const toggleExpand = (id) => setExpanded((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const exportJobs = () => {
    const rows = [];
    for (const j of shownJobs) {
      const base = {
        clientCode: j.clientCode, clientName: j.clientName, jobName: j.jobName, idd: j.idd,
        jm: j.managerName, jp: j.partnerName, budget: j.budgetHours, wip: j.wipTotal,
        remaining: j.remaining, flags: j.flags.map((f) => f.label).join('; '),
      };
      if (!j.staff.length && !j.placeholders.length) rows.push({ ...base, who: '', weekly: j.weekly, total: j.total });
      for (const s of j.staff) rows.push({ ...base, who: s.name + (s.isActive ? '' : ' (inactive)'), weekly: s.weekly, total: s.total });
      for (const p of j.placeholders) rows.push({ ...base, who: `Placeholder · ${p.dept} · ${p.level}`, weekly: p.weekly, total: p.total });
    }
    exportCsv('rprs-4-week-lookahead', rows, [
      { label: 'Client code', get: (r) => r.clientCode },
      { label: 'Client', get: (r) => r.clientName },
      { label: 'Job', get: (r) => r.jobName },
      { label: 'Internal due date', get: (r) => r.idd },
      { label: 'Job manager', get: (r) => r.jm },
      { label: 'Job partner', get: (r) => r.jp },
      { label: 'Scheduled', get: (r) => r.who },
      ...weekDates.map((w, i) => ({ label: `Week of ${w}`, get: (r) => r.weekly[i] })),
      { label: `${weekDates.length}-week total`, get: (r) => r.total },
      { label: 'Budget', get: (r) => r.budget },
      { label: 'Charged to date', get: (r) => r.wip },
      { label: 'Remaining', get: (r) => r.remaining },
      { label: 'Flags', get: (r) => r.flags },
    ]);
  };

  const colCount = 7 + weekDates.length + 5;

  return (
    <div className="sched-rpt-root">
      <div className="sched-cap-toolbar" style={{ flexWrap: 'wrap', rowGap: 6 }}>
        <span className="sched-cap-toolbar-label" title="Same data, two shapes.">VIEW</span>
        <button className={`sched-mgr-pill${view === 'job' ? ' sched-mgr-pill--active' : ''}`}
          onClick={() => setView('job')} title="One row per job, expandable to who is scheduled on it.">By job</button>
        <button className={`sched-mgr-pill${view === 'person' ? ' sched-mgr-pill--active' : ''}`}
          onClick={() => setView('person')} title="One row per person scheduled on these jobs, with everything else they are scheduled for.">By person</button>

        <span style={{ width: 12 }} />
        <span className="sched-cap-toolbar-label" title="Which of your jobs to include.">ROLE</span>
        {[['both', 'JM + JP'], ['jm', 'JM'], ['jp', 'JP']].map(([k, label]) => (
          <button key={k} className={`sched-mgr-pill${role === k ? ' sched-mgr-pill--active' : ''}`}
            onClick={() => setRole(k)}
            title={k === 'both' ? 'Jobs where you are job manager or job partner.'
              : k === 'jm' ? 'Only jobs where you are the job manager. This matches the Monday email.'
                : 'Only jobs where you are the job partner.'}>{label}</button>
        ))}

        <span style={{ width: 12 }} />
        <span className="sched-cap-toolbar-label" title="How many weeks ahead, starting with this week.">WEEKS</span>
        {WEEK_OPTIONS.map((w) => (
          <button key={w} className={`sched-mgr-pill${weeks === w ? ' sched-mgr-pill--active' : ''}`}
            onClick={() => setWeeks(w)} title={`This week and the ${w - 1} after it.`}>{w}</button>
        ))}

        {data?.isSuper && (
          <>
            <span style={{ width: 12 }} />
            <span className="sched-cap-toolbar-label" title="Super-users can see any job lead's view, exactly as that person sees it.">VIEW AS</span>
            <select className="sched-rpt-bsel" value={lead} onChange={(e) => setLead(e.target.value)}>
              <option value="">Whole firm</option>
              {data.meStaffId != null && <option value={String(data.meStaffId)}>Me</option>}
              {(data.leads || []).map((l) => (
                <option key={l.staffId} value={String(l.staffId)}>
                  {l.name}{l.isActive ? '' : ' (inactive)'} — JM {l.jmJobs} · JP {l.jpJobs}
                </option>
              ))}
            </select>
          </>
        )}

        <input className="sched-rpt-btext" style={{ marginLeft: 'auto', minWidth: 180 }}
          placeholder="Search client, job, person…" value={q} onChange={(e) => setQ(e.target.value)}
          aria-label="Search" />
        {view === 'job' && shownJobs.length > 0 && (
          <button className="sched-rpt-csv" onClick={exportJobs}
            title="Download what is shown, one row per person per job.">CSV</button>
        )}
      </div>

      {error && <div className="sched-rpt-error">Couldn&rsquo;t load: {error}</div>}
      {loading && <div className="sched-rpt-muted">Loading&hellip;</div>}

      {!loading && !error && data && (
        <>
          <div className="sched-rpt-summary">
            <div className="sched-rpt-statgroup">
              <div className="sched-rpt-statgroup-l">
                Weeks of {shortDay(weekStart)} &ndash; {shortDay(weekDates[weekDates.length - 1])}
              </div>
              <div className="sched-rpt-statrow">
                <div className="sched-rpt-stat" title="Jobs listed below.">
                  <div className="sched-rpt-stat-n">{stats.jobs}</div>
                  <div className="sched-rpt-stat-l">Jobs</div>
                </div>
                <div className="sched-rpt-stat" title="Hours scheduled on the jobs listed, across the weeks shown.">
                  <div className="sched-rpt-stat-n">{stats.hours}</div>
                  <div className="sched-rpt-stat-l">Hours</div>
                </div>
                <div className="sched-rpt-stat" title="People scheduled on the jobs listed.">
                  <div className="sched-rpt-stat-n">{stats.staff}</div>
                  <div className="sched-rpt-stat-l">People</div>
                </div>
                <div className="sched-rpt-stat" title="Jobs listed with at least one flag.">
                  <div className="sched-rpt-stat-n" style={{ color: stats.flagged ? '#B45309' : undefined }}>{stats.flagged}</div>
                  <div className="sched-rpt-stat-l">Flagged</div>
                </div>
              </div>
            </div>

            <div className="sched-rpt-summary-note">
              Week columns are whole weeks (Mon&ndash;Sun), this week included. <strong>Budget</strong>,{' '}
              <strong>Left</strong> and the flags count every hour scheduled <strong>from today on</strong>,
              however far out &mdash; the same figure as the My Jobs budget bar.
            </div>

            <div className="sched-rpt-flagbox">
              <div className="sched-rpt-flagbox-chips">
                {FLAG_CODES.map((code) => (
                  <FlagChip key={code} code={code} count={flagCounts[code]} active={flagCode === code}
                    onClick={() => setFlagCode(flagCode === code ? '' : code)} />
                ))}
              </div>
              <label className="sched-rpt-check" title="Show only jobs with at least one flag.">
                <input type="checkbox" checked={flagsOnly} onChange={(e) => setFlagsOnly(e.target.checked)} />
                {' '}Flagged only
              </label>
            </div>
          </div>

          {data.jobs.length === 0 ? (
            <div className="sched-rpt-muted" style={{ padding: '18px 2px' }}>
              {data.scope === 'firm' && !data.viewingAs
                ? <>Nothing is scheduled on any job in these weeks.</>
                : <>Nothing is scheduled on {data.viewingAs && !data.viewingAs.self ? 'these' : 'your'} jobs for the
                    next {weekDates.length} weeks, and none of them is due in that window with nothing scheduled.</>}
            </div>
          ) : view === 'job' ? (
            <div className="sched-mgr-table-wrap sched-rpt-scroll">
              <table className="sched-mgr-table sched-rpt-table">
                <thead>
                  <tr className="sched-rpt-colrow">
                    <PlainTh label="" title="Show who is scheduled on the job." />
                    <SortableTh sort={sort} sortKey="client" label="Code" title="Client code." />
                    <SortableTh sort={sort} sortKey="clientName" label="Client" title="Client name." />
                    <SortableTh sort={sort} sortKey="job" label="Job"
                      title="Click a job to open it on the schedule, ready to edit." />
                    <SortableTh sort={sort} sortKey="idd" label="IDD" title="Internal due date." />
                    <SortableTh sort={sort} sortKey="jm" label="JM / JP" title="Job manager and job partner." />
                    <PlainTh label="Milestone" title="Where the job sits in the workflow." />
                    {weekDates.map((w) => (
                      <PlainTh key={w} label={shortDay(w)} className="sched-mgr-th-right"
                        title={`Hours scheduled in the week of ${w} (Monday to Sunday).`} />
                    ))}
                    <SortableTh sort={sort} sortKey="total" label="Total" className="sched-mgr-th-right"
                      title="Hours scheduled across the weeks shown." />
                    <SortableTh sort={sort} sortKey="budget" label="Budget" className="sched-mgr-th-right"
                      title="The job's budgeted hours." />
                    <SortableTh sort={sort} sortKey="wip" label="Charged" className="sched-mgr-th-right"
                      title="Hours charged to the job so far." />
                    <SortableTh sort={sort} sortKey="remaining" label="Left" className="sched-mgr-th-right"
                      title="Budget, less hours charged, less every hour scheduled from today on. Negative means the plan already runs over budget." />
                    <SortableTh sort={sort} sortKey="flags" label="Flags" title="Anything about this job's time that may need a look." />
                  </tr>
                </thead>
                <tbody>
                  {shownJobs.map((j) => {
                    const open = expanded.has(j.jobId);
                    const hasDetail = j.staff.length > 0 || j.placeholders.length > 0;
                    const closedWeek = j.firstFutureDate ? mondayOf(j.firstFutureDate) : weekStart;
                    return (
                      <Fragment key={j.jobId}>
                        <tr>
                          <td style={{ width: 22 }}>
                            {hasDetail && (
                              <button className="sched-rpt-disclose" style={{ padding: 0 }}
                                onClick={() => toggleExpand(j.jobId)}
                                title={open ? 'Hide who is scheduled' : 'Show who is scheduled'}>
                                {open ? '▾' : '▸'}
                              </button>
                            )}
                          </td>
                          <td className="sched-rpt-muted-sm">{j.clientCode}</td>
                          <td>{j.clientName}</td>
                          <td>
                            {j.closedInPE ? (
                              <>
                                <span className="sched-grid-detail-link"
                                  onClick={() => navigate('/schedule/dashboard?tab=myjobs')}
                                  title="This job is completed in Practice Engine. Opens My Jobs, where 'Completed with scheduled time' can clear the leftover hours in one click.">
                                  {j.jobName}
                                </span>
                                {' '}
                                <span className="sched-grid-detail-link sched-rpt-muted-sm"
                                  onClick={() => navigate(gridJobHref(j, closedWeek, weeks))}
                                  title="Open the schedule at the first week this job still has time.">(schedule)</span>
                              </>
                            ) : (
                              <span className="sched-grid-detail-link"
                                onClick={() => navigate(gridJobHref(j, weekStart, weeks))}
                                title="Open this job on the schedule, ready to edit.">
                                {j.jobName}
                              </span>
                            )}
                            {j.poolState && (
                              <span className="sched-rpt-chip" style={{ background: '#E0F2FE', color: '#075985', marginLeft: 6 }}
                                title={j.poolState === 'waiting'
                                  ? 'Sitting in a preparer pool, waiting to be claimed.'
                                  : 'Claimed from a preparer pool.'}>
                                {j.poolState === 'waiting' ? 'In pool' : 'From pool'}
                              </span>
                            )}
                          </td>
                          <td className="sched-rpt-muted-sm">{shortDay(j.idd)}</td>
                          <td className="sched-rpt-muted-sm">
                            {j.managerName || '—'}
                            {j.partnerName && j.partnerName !== j.managerName && <><br />{j.partnerName}</>}
                          </td>
                          <td className="sched-rpt-muted-sm">
                            {j.closedInPE
                              ? <span className="sched-rpt-chip" style={{ background: '#E5E7EB', color: '#374151' }}
                                  title="Completed in Practice Engine.">Closed</span>
                              : j.phase >= 0
                                ? <span className="sched-rpt-chip" style={{ background: PHASE_COLORS[j.phase], color: '#fff' }}
                                    title={j.statusDetail || ''}>{PHASE_LABELS[j.phase]}</span>
                                : <span title={j.statusDetail || ''}>{j.statusDetail || '—'}</span>}
                          </td>
                          <WeekCells weekly={j.weekly} />
                          <td className="sched-mgr-td-right" style={{ fontWeight: 600 }}>{hrs(j.total) || '—'}</td>
                          <td className="sched-mgr-td-right sched-rpt-muted-sm">{hrs(j.budgetHours) || '—'}</td>
                          <td className="sched-mgr-td-right sched-rpt-muted-sm">{hrs(j.wipTotal) || '0'}</td>
                          <td className="sched-mgr-td-right"
                            style={{ color: j.remaining != null && j.remaining < 0 ? '#B91C1C' : undefined }}>
                            {j.remaining == null ? '—' : (hrs(j.remaining) || '0')}
                          </td>
                          <td style={{ whiteSpace: 'nowrap' }}>
                            {j.flags.map((f) => <FlagChip key={f.code} code={f.code} detail={f.detail} />)}
                            {j.flags.some((f) => f.code === 'nothing_scheduled') && (
                              <span className="sched-grid-detail-link sched-rpt-muted-sm"
                                onClick={() => navigate(`/schedule/suggestions/${j.jobId}`)}
                                title="See who would be a good fit for this job.">Suggestions</span>
                            )}
                          </td>
                        </tr>
                        {open && j.staff.map((s) => (
                          <tr key={`${j.jobId}_s${s.staffId}`} style={{ background: '#F9FAFB' }}>
                            <td />
                            <td colSpan={6} style={{ paddingLeft: 18 }}>
                              <span className="sched-grid-detail-link"
                                onClick={() => navigate(`/schedule/employee/${s.staffId}`)}>{s.name}</span>
                              <span className="sched-rpt-muted-sm">{s.level ? ` · ${s.level}` : ''}</span>
                              {!s.isActive && (
                                <span className="sched-rpt-chip" style={{ background: '#FECACA', color: '#7F1D1D', marginLeft: 6 }}
                                  title="No longer works here.">Inactive</span>
                              )}
                            </td>
                            <WeekCells weekly={s.weekly} />
                            <td className="sched-mgr-td-right">{hrs(s.total)}</td>
                            <td colSpan={4} />
                          </tr>
                        ))}
                        {open && j.placeholders.map((p) => (
                          <tr key={`${j.jobId}_p${p.placeholderId}`} style={{ background: '#FFFBEB' }}>
                            <td />
                            <td colSpan={6} style={{ paddingLeft: 18, fontStyle: 'italic', color: '#B45309' }}
                              title={`Hours held for a ${p.level} in ${p.dept}, not yet assigned to a person: ${p.hours}h from ${p.startDate} to ${p.endDate}, spread evenly across the weekdays.`}>
                              Placeholder · {p.dept} · {p.level}
                            </td>
                            <WeekCells weekly={p.weekly} />
                            <td className="sched-mgr-td-right" style={{ fontStyle: 'italic', color: '#B45309' }}>{hrs(p.total)}</td>
                            <td colSpan={4} />
                          </tr>
                        ))}
                      </Fragment>
                    );
                  })}
                  {shownJobs.length === 0 && (
                    <tr><td colSpan={colCount} className="sched-rpt-muted">No jobs match these filters.</td></tr>
                  )}
                  {shownJobs.length > 0 && (
                    <tr style={{ borderTop: `2px solid ${BRAND.deepTeal}` }}>
                      <td />
                      <td colSpan={6} style={{ fontWeight: 700, color: BRAND.deepTeal }}>
                        {stats.jobs} job{stats.jobs === 1 ? '' : 's'}
                      </td>
                      {stats.weekTotals.map((h, i) => (
                        <td key={i} className="sched-mgr-td-right" style={{ fontWeight: 700, color: BRAND.deepTeal }}>{hrs(h) || '0'}</td>
                      ))}
                      <td className="sched-mgr-td-right" style={{ fontWeight: 700, color: BRAND.deepTeal }}>{hrs(stats.hours) || '0'}</td>
                      <td colSpan={4} />
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="sched-mgr-table-wrap sched-rpt-scroll">
              <table className="sched-mgr-table sched-rpt-table">
                <thead>
                  <tr className="sched-rpt-colrow">
                    <PlainTh label="" title="Show this person's jobs." />
                    <SortableTh sort={personSort} sortKey="name" label="Person" title="Someone scheduled on these jobs." />
                    <SortableTh sort={personSort} sortKey="level" label="Level" title="Staff level." />
                    {weekDates.map((w) => (
                      <PlainTh key={w} label={shortDay(w)} className="sched-mgr-th-right"
                        title={`Week of ${w}. First number: hours on these jobs. Second: everything this person is scheduled for that week, on every job. For information only - it is not a capacity measure.`} />
                    ))}
                    <SortableTh sort={personSort} sortKey="mine" label="On these" className="sched-mgr-th-right"
                      title="Hours on these jobs across the weeks shown." />
                    <SortableTh sort={personSort} sortKey="all" label="All jobs" className="sched-mgr-th-right"
                      title="Everything this person is scheduled for across the weeks shown, on every job. For information only." />
                    <SortableTh sort={personSort} sortKey="jobs" label="Jobs" className="sched-mgr-th-right"
                      title="How many of these jobs they are on." />
                  </tr>
                </thead>
                <tbody>
                  {shownPeople.map((p) => {
                    const key = `p${p.staffId}`;
                    const open = expanded.has(key);
                    return (
                      <Fragment key={key}>
                        <tr>
                          <td style={{ width: 22 }}>
                            <button className="sched-rpt-disclose" style={{ padding: 0 }}
                              onClick={() => toggleExpand(key)}
                              title={open ? 'Hide their jobs' : 'Show their jobs'}>{open ? '▾' : '▸'}</button>
                          </td>
                          <td>
                            <span className="sched-grid-detail-link"
                              onClick={() => navigate(`/schedule/employee/${p.staffId}`)}>{p.name}</span>
                            {!p.isActive && (
                              <span className="sched-rpt-chip" style={{ background: '#FECACA', color: '#7F1D1D', marginLeft: 6 }}
                                title="No longer works here.">Inactive</span>
                            )}
                          </td>
                          <td className="sched-rpt-muted-sm">{p.level || '—'}</td>
                          {p.weeklyMine.map((h, i) => (
                            <td key={i} className="sched-mgr-td-right" style={{ whiteSpace: 'nowrap' }}>
                              {h || p.weeklyAll[i]
                                ? <><span style={{ fontWeight: 600 }}>{hrs(h) || '0'}</span>
                                    <span className="sched-rpt-muted-sm"> / {hrs(p.weeklyAll[i]) || '0'}</span></>
                                : ''}
                            </td>
                          ))}
                          <td className="sched-mgr-td-right" style={{ fontWeight: 600 }}>{hrs(p.totalMine)}</td>
                          <td className="sched-mgr-td-right sched-rpt-muted-sm">{hrs(p.totalAll)}</td>
                          <td className="sched-mgr-td-right sched-rpt-muted-sm">{p.jobCount}</td>
                        </tr>
                        {open && p.jobs.map((jj) => (
                          <tr key={`${key}_${jj.jobId}`} style={{ background: '#F9FAFB' }}>
                            <td />
                            <td colSpan={2} style={{ paddingLeft: 18 }}>
                              <span className="sched-grid-detail-link"
                                onClick={() => navigate(gridJobHref(jj, weekStart, weeks))}
                                title="Open this job on the schedule.">{jj.clientName} · {jj.jobName}</span>
                            </td>
                            <WeekCells weekly={jj.weekly} />
                            <td className="sched-mgr-td-right">{hrs(jj.total)}</td>
                            <td colSpan={2} />
                          </tr>
                        ))}
                      </Fragment>
                    );
                  })}
                  {shownPeople.length === 0 && (
                    <tr><td colSpan={6 + weekDates.length} className="sched-rpt-muted">No one matches this search.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
